/**
 * "Your avatars" in a world (2D): pick any avatar you own and change into it
 * without leaving (services/avatarSwitch), or "Get a new avatar", which opens
 * the regular create flow over the world. <AvatarSwitchLayer> mounts once in
 * the viewer; in VR the in-headset panel (VRAvatarSwitch) is used instead.
 */
import React, { useEffect, useState } from 'react';
import { Check, Loader, Plus, Shirt, UserRound, X } from 'lucide-react';
import { useWorldStore } from '../../stores/worldStore';
import { avatarPicture, closeAvatarPicker, openAvatarCreate, refreshMyAvatars, switchAvatar, useAvatarSwitch } from '../../services/avatarSwitch';
import type { Avatar } from '../../services/avatarService';
import './AvatarSwitch.css';

function Picture({ a }: { a: Avatar }) {
  const [failed, setFailed] = useState(false);
  const src = avatarPicture(a);
  return <div className="as-pic">
    {src && !failed
      ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      : <span className="as-pic-empty"><UserRound size={30} /></span>}
  </div>;
}

export function AvatarSwitchPanel() {
  const open = useAvatarSwitch((s) => s.pickerOpen);
  const avatars = useAvatarSwitch((s) => s.avatars);
  const error = useAvatarSwitch((s) => s.error);
  const switchingId = useAvatarSwitch((s) => s.switchingId);
  const activeId = useWorldStore((s) => s.activeAvatarId);
  const activeUrl = useWorldStore((s) => s.avatarUrl);
  const signedIn = useWorldStore((s) => !!s.user?.id);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAvatarPicker(); };
    window.addEventListener('keydown', onKey);
    if (document.pointerLockElement) document.exitPointerLock?.();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open) return null;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const worn = (a: Avatar) => (activeId ? a.id === activeId : a.model_url === activeUrl);
  const switching = avatars?.find((a) => a.id === switchingId);

  return <div className="as-scrim" onClick={closeAvatarPicker} onPointerDown={stop}>
    <section className="as-panel" role="dialog" aria-modal="true" aria-label="Your avatars"
      onClick={stop} onPointerDown={stop} onKeyDown={stop} onKeyUp={stop} onWheel={stop}>
      <header className="as-head">
        <span className="as-head-icon"><Shirt size={18} /></span>
        <h2>Your avatars</h2>
        <button className="as-icon-btn" onClick={closeAvatarPicker} aria-label="Close"><X size={18} /></button>
      </header>
      <p className="as-sub">
        {switching ? <><Loader size={14} className="spin" /> Changing into {switching.name}…</> : 'Change who you are here. Everyone in the world sees the switch.'}
      </p>
      <div className="as-list">
        {avatars === null && <p className="as-note"><Loader size={15} className="spin" /> Loading your avatars…</p>}
        {error && <p className="as-note as-note--err">{error} <button className="as-link" onClick={() => void refreshMyAvatars()}>Retry</button></p>}
        {!signedIn && avatars !== null && avatars.length === 0 && <p className="as-note">Sign in to keep a wardrobe of avatars you can switch between.</p>}
        <div className="as-grid">
          <button className="as-card as-card--new" onClick={openAvatarCreate} data-as="new">
            <span className="as-new-icon"><Plus size={22} /></span>
            <span className="as-card-name">Get a new avatar</span>
            <span className="as-card-meta">Free robot or free Avatar Builder</span>
          </button>
          {(avatars || []).map((a) => {
            const on = worn(a);
            const busy = switchingId === a.id;
            return <button key={a.id} className={`as-card${on ? ' is-on' : ''}${busy ? ' is-busy' : ''}`}
              disabled={!!switchingId || on} aria-pressed={on} onClick={() => void switchAvatar(a)} data-avatar={a.id}>
              <Picture a={a} />
              {on && <span className="as-badge"><Check size={11} /> Wearing</span>}
              {busy && <span className="as-busy"><Loader size={22} className="spin" /></span>}
              <span className="as-card-name">{a.name}</span>
              <span className="as-card-meta">{on ? 'You, right now' : busy ? 'Loading…' : 'Change into'}</span>
            </button>;
          })}
        </div>
      </div>
    </section>
  </div>;
}

export function AvatarSwitchToast() {
  const toast = useAvatarSwitch((s) => s.toast);
  if (!toast) return null;
  return <div className="as-toast" role="status">{toast}</div>;
}

/** Everything 2D, mounted once in the viewer. */
export function AvatarSwitchLayer({ inVR }: { inVR: boolean }) {
  // Leaving the world closes the panels (the store outlives the page).
  useEffect(() => () => useAvatarSwitch.setState({ pickerOpen: false, createOpen: false }), []);
  if (inVR) return null; // VRAvatarSwitch draws the panel and notices in the headset
  return <>
    <AvatarSwitchPanel />
    <AvatarSwitchToast />
  </>;
}
